'use client';
import classNames from 'classnames';
import { memo, useEffect, useRef, useState } from 'react';

import { useRouteChange } from '@/hooks/useRouteChange';

export interface TabItem<K extends string = string> {
  key: K;
  label: string | React.ReactNode | ((activeKey: K, data: TabItem<K>) => React.ReactNode);
  icon?: React.ReactNode;
  disabled?: boolean;
  // 埋点相关
  eventTracking?: {
    [key: string]: any;
  };
}

export interface TabProps<T extends TabItem<K>[], K extends string = string> {
  items: T;
  activeKey: K;
  disabled?: boolean;
  onChange?: (key: K) => void;
  className?: string;
  itemClassName?: string;
  sliderClassName?: string;
  action?: React.ReactNode;
  type?: 'default' | 'slider';
  sliderType?: 'full' | 'underline';
}

const Tab = memo(
  <T extends TabItem<K>[], K extends string = string>({
    items,
    activeKey,
    disabled,
    onChange,
    className,
    itemClassName,
    sliderClassName,
    action,
    type = 'default',
    sliderType = 'full',
  }: TabProps<T, K>) => {
    const { navigate } = useRouteChange();
    const [indicatorStyle, setIndicatorStyle] = useState({
      height: 0,
      width: 0,
      left: 0,
    });
    const tabItemsRef = useRef<HTMLDivElement[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
      if (type === 'slider') {
        const activeIndex = items.findIndex((item) => item.key === activeKey);
        if (activeIndex !== -1) {
          const activeItemRef = tabItemsRef.current[activeIndex];
          const itemHeight = activeItemRef?.offsetHeight || 0;
          const itemWidth = activeItemRef?.offsetWidth || 0;
          const itemLeft = activeItemRef?.offsetLeft || 0;
          setIndicatorStyle({
            height: sliderType === 'full' ? itemHeight : 2, // 如果是 'underline'，高度为 2
            width: itemWidth,
            left: itemLeft,
          });
          if (!isInitialized) {
            setIsInitialized(true);
          }
        }
      }
    }, [activeKey, items, type, isInitialized, sliderType]);

    const handleTabClick = (
      key: T[number]['key'],
      eventTracking?: { [key: string]: any },
    ) => {
      if (disabled) {
        return;
      }
      if (activeKey === key) return;
      navigate(() => {
        onChange?.(key);
      });
    };

    const renderLabel = (item: TabItem<T[number]['key']>) => {
      const { label } = item;
      if (typeof label === 'function') {
        return label(activeKey, item);
      }
      return label;
    };

    return (
      <div className="flex justify-between items-center">
        <div
          className={classNames('flex', className, {
            relative: type === 'slider',
          })}
        >
          {items.map((item: TabItem<T[number]['key']>, index: number) => {
            const { key, disabled, eventTracking } = item;
            return (
              <div
                key={key}
                ref={(el) => {
                  tabItemsRef.current[index] = el as HTMLDivElement;
                }}
                className={classNames(
                  'z-[2] select-none flex justify-center items-center text-common-text-base-color cursor-pointer relative',
                  {
                    'opacity-100': key === activeKey,
                    'opacity-60': key !== activeKey && type === 'default',
                  },
                  itemClassName,
                )}
                onClick={() => handleTabClick(key, eventTracking)}
              >
                {renderLabel(item)}
              </div>
            );
          })}
          {type === 'slider' && isInitialized && (
            <div
              className={classNames(
                'absolute transition-all duration-300 z-[1]',
                sliderClassName,
                {
                  'bottom-0': sliderType === 'underline',
                },
              )}
              style={indicatorStyle}
            />
          )}
        </div>
        {action ? <div className="operation">{action}</div> : null}
      </div>
    );
  },
) as <T extends TabItem<K>[], K extends string = string>(
  props: TabProps<T, K>,
) => React.ReactElement;

export default Tab;
